import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, ListChecks, Clock, Target } from "lucide-react";

import { ArticleTOC } from "@/components/resources/ArticleTOC";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";

const sections = [
  { id: "before-you-start", title: "Before You Start" },
  { id: "during-session", title: "During Your Session" },
  { id: "after-session", title: "After Your Session" },
  { id: "quick-reference", title: "Quick Reference: Study Techniques" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

const StudyChecklist = () => {
  useSEO({
    title: "Study Session Checklist: Optimize Every Session | System Atlas",
    description: "A practical checklist for effective study sessions. Use this template before, during, and after studying to maximize retention and minimize wasted time.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/study-checklist" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/study-checklist" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/study-checklist" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="Study Session Checklist: Optimize Every Session" description="A practical checklist for effective study sessions." publishedDate="2026-02-08" modifiedDate="2026-02-21" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Learning Guides</Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">Study Session Checklist</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">Use this checklist before, during, and after every study session. It incorporates evidence-based learning techniques to help you retain more in less time.</p>

          <ArticleTOC sections={sections} />

          <section id="before-you-start" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-primary" /> Before You Start</h2>
            <div className="space-y-2">
              {[
                { title: "Define your goal", desc: "What specific concept or skill will you master?" },
                { title: "Remove distractions", desc: "Phone on silent, notifications off, browser tabs closed" },
                { title: "Gather materials", desc: "Notes, textbook, revision sheets, blank paper" },
                { title: "Set a timer", desc: "25-50 minutes focused work, then a short break" },
                { title: "Review previous session", desc: "Quickly recall what you learned last time" },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-3 border border-border/60 rounded-lg hover:bg-muted/20 transition-colors">
                  <div className="w-4 h-4 border-2 border-primary rounded shrink-0 mt-0.5" />
                  <div><p className="font-medium text-sm">{item.title}</p><p className="text-xs text-muted-foreground">{item.desc}</p></div>
                </div>
              ))}
            </div>
          </section>

          <section id="during-session" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-primary" /> During Your Session</h2>
            <div className="space-y-2">
              {[
                { title: "Read actively, not passively", desc: "Ask questions, make predictions, connect to what you know" },
                { title: "Practice active recall", desc: "Close your materials and write what you remember" },
                { title: "Explain concepts aloud", desc: "Use the Feynman Technique" },
                { title: "Take strategic notes", desc: "Focus on concepts, not transcription" },
                { title: "Work through problems", desc: "Attempt problems before checking answers" },
                { title: "Mark confusion points", desc: "Note what's unclear for follow-up" },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-3 border border-border/60 rounded-lg hover:bg-muted/20 transition-colors">
                  <div className="w-4 h-4 border-2 border-primary rounded shrink-0 mt-0.5" />
                  <div><p className="font-medium text-sm">{item.title}</p><p className="text-xs text-muted-foreground">{item.desc}</p></div>
                </div>
              ))}
            </div>
          </section>

          <section id="after-session" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><ListChecks className="w-5 h-5 text-primary" /> After Your Session</h2>
            <div className="space-y-2">
              {[
                { title: "Summarize from memory", desc: "Write 3-5 key points you learned without looking" },
                { title: "Identify knowledge gaps", desc: "What couldn't you recall?" },
                { title: "Plan your next session", desc: "Schedule when you'll review again (1-3 days)" },
                { title: "Take a real break", desc: "Step away from screens. Your brain consolidates during rest." },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-3 border border-border/60 rounded-lg hover:bg-muted/20 transition-colors">
                  <div className="w-4 h-4 border-2 border-primary rounded shrink-0 mt-0.5" />
                  <div><p className="font-medium text-sm">{item.title}</p><p className="text-xs text-muted-foreground">{item.desc}</p></div>
                </div>
              ))}
            </div>
          </section>

          <section id="quick-reference" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Quick Reference: Study Techniques</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border"><th className="text-left py-3 px-4 font-medium">Technique</th><th className="text-left py-3 px-4 font-medium">When to Use</th><th className="text-left py-3 px-4 font-medium">Effectiveness</th></tr></thead>
                <tbody className="text-muted-foreground text-xs">
                  <tr className="border-b border-border/50"><td className="py-3 px-4 font-medium text-foreground">Active Recall</td><td className="py-3 px-4">After reading new material</td><td className="py-3 px-4"><span className="text-primary font-medium">High</span></td></tr>
                  <tr className="border-b border-border/50"><td className="py-3 px-4 font-medium text-foreground">Spaced Repetition</td><td className="py-3 px-4">For long-term retention</td><td className="py-3 px-4"><span className="text-primary font-medium">High</span></td></tr>
                  <tr className="border-b border-border/50"><td className="py-3 px-4 font-medium text-foreground">Feynman Technique</td><td className="py-3 px-4">Complex or abstract concepts</td><td className="py-3 px-4"><span className="text-primary font-medium">High</span></td></tr>
                  <tr className="border-b border-border/50"><td className="py-3 px-4 font-medium text-foreground">Practice Problems</td><td className="py-3 px-4">Math, science, procedures</td><td className="py-3 px-4"><span className="text-primary font-medium">High</span></td></tr>
                  <tr className="border-b border-border/50"><td className="py-3 px-4 font-medium text-foreground">Re-reading</td><td className="py-3 px-4">Quick refresh only</td><td className="py-3 px-4"><span className="text-muted-foreground font-medium">Low</span></td></tr>
                  <tr><td className="py-3 px-4 font-medium text-foreground">Highlighting</td><td className="py-3 px-4">Marking for later review</td><td className="py-3 px-4"><span className="text-muted-foreground font-medium">Low</span></td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="key-takeaways" className="p-6 bg-primary/5 border border-primary/15 rounded-xl">
            <h2 className="text-lg font-medium mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Set a specific goal before each session</li>
              <li>• Active techniques beat passive review every time</li>
              <li>• Test yourself frequently, not just before exams</li>
              <li>• Space your practice over multiple days</li>
              <li>• Track your confusion. it shows where to focus</li>
              <li>• Rest is part of learning, not the opposite of it</li>
            </ul>
          </section>
        </article>

        <nav className="mt-12 pt-8 border-t border-border/40">
          <h3 className="font-medium mb-4">Related Learning Guides</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/learn/active-recall" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">Active Recall Explained</h4><p className="text-xs text-muted-foreground">The science behind effective learning</p></Link>
            <Link to="/learn/feynman-technique" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">The Feynman Technique</h4><p className="text-xs text-muted-foreground">Learn anything by teaching it simply</p></Link>
          </div>
        </nav>
      </div>
    </>
  );
};

export default StudyChecklist;
